import type { FC } from 'react';
import {
    Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useChartTokens, useTooltipStyle } from './ChartKit';
import type { VisualAssistente } from '../services/AssistenteService';
import '../pages/AssistenteLayout.css';

/**
 * O gráfico que acompanha uma resposta do assistente.
 *
 * <para><b>Os pontos vêm da ferramenta que consultou o dado</b>, não do texto do
 * modelo — se o modelo descrevesse a série, ele poderia desenhar números
 * diferentes dos que acabou de escrever na frase, e o usuário teria dois
 * valores conflitantes na mesma bolha.</para>
 *
 * <para>Só existe para o que a <b>barra dentro da linha da tabela</b> não cobre:
 * série ao longo do tempo. Ranking continua sendo tabela com barra na linha —
 * é o padrão de tela, e um gráfico à parte repetiria a mesma informação.</para>
 *
 * <para>Cores do <code>ChartKit</code>: volume é <b>uma cor só</b>
 * (<code>tokens.primary</code>), como manda o padrão de gráficos.</para>
 */

const moedaCurta = (valor: number) => {
    if (Math.abs(valor) >= 1000000) return `R$ ${(valor / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}mi`;
    if (Math.abs(valor) >= 1000) return `R$ ${(valor / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
    return `R$ ${valor.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const moedaCheia = (valor: number) =>
    valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const AssistenteGrafico: FC<{ visual: VisualAssistente }> = ({ visual }) => {
    const tokens = useChartTokens();
    const tooltip = useTooltipStyle();

    if (!visual.pontos?.length) return null;

    const ehMoeda = visual.formato === 'moeda';

    return (
        <figure className="as-graf">
            <figcaption className="as-graf-tit">{visual.titulo}</figcaption>

            <ResponsiveContainer width="100%" height={168}>
                <BarChart data={visual.pontos} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    {/* Só a grade horizontal: linha vertical em gráfico de barras
                        duplica a própria barra como referência. */}
                    <CartesianGrid stroke={tokens.grid} vertical={false} />
                    <XAxis
                        dataKey="rotulo"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: tokens.axis, fontSize: 11 }}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        width={ehMoeda ? 52 : 34}
                        tick={{ fill: tokens.axis, fontSize: 11 }}
                        tickFormatter={(v: number) => (ehMoeda ? moedaCurta(v) : String(v))}
                    />
                    <Tooltip
                        {...tooltip}
                        cursor={{ fill: tokens.grid }}
                        /* Rótulo curto: com o título inteiro o tooltip virava
                           "Faturamento dos últimos 6 meses : R$ 0,00". */
                        formatter={(v: number) => [ehMoeda ? moedaCheia(v) : v, visual.rotuloSerie ?? 'Valor']}
                    />
                    <Bar dataKey="valor" fill={tokens.primary} radius={[3, 3, 0, 0]} maxBarSize={44} />
                </BarChart>
            </ResponsiveContainer>
        </figure>
    );
};

export default AssistenteGrafico;
